import { API, Storage, graphqlOperation } from "aws-amplify";
import * as mutations from "../graphql/mutations";
import * as idOnlyMutations from "../graphql/mutations-ids-only";
import * as queries from "../graphql/queries";
import * as listIdQueries from "../graphql/list-id-queries";

import { AppType, ResourceType, UserType } from "../utils/constants";
import {
  getEnv,
  isConditionalCheckFailed,
  logError,
  safeAPI,
} from "../utils/appUtils";
import { normalizeCarrierUserType } from "../utils/carrierUserUtils";
import {
  RESET_PAGE_LIMIT,
  buildResetListVariables,
  flattenGraphqlError,
  runPromisesInConcurrencyChunks,
} from "./resetListVariables";

// ########################################################
// ########################################################
// DELETE OPERATIONS
// ########################################################
// ########################################################

const resetProductDerivedData = async (reloadYardId) => {
  const items = await listResetItems(
    listIdQueries.listProductIds,
    "listProducts",
    reloadYardId,
  );

  await runResetMutationBatch("Reset Product derived data", items, (item) =>
    graphqlOperation(idOnlyMutations.updateIdOnly("Product"), {
      input: {
        id: item.id,
        totalQuantityOnHand: 0,
        totalQuantityCommitted: 0,
        lowInventoryAlert: 0 <= Number(item.lowInventoryThreshold || 0),
      },
    }),
  );
};

/** Dev reset only: delete Checklist rows whose resourceId is an inbound or outbound shipment id for this yard. Preserves account/commodity and other non-shipment checklists. */
const deleteShipmentLinkedChecklistsForReset = async (reloadYardId) => {
  const [inboundItems, outboundItems] = await Promise.all([
    listResetItems(
      listIdQueries.listInboundShipmentIds,
      "listInboundShipments",
      reloadYardId,
    ),
    listResetItems(
      listIdQueries.listOutboundShipmentIds,
      "listOutboundShipments",
      reloadYardId,
    ),
  ]);
  const shipmentResourceIds = new Set(
    [...inboundItems, ...outboundItems].map((row) => row.id).filter(Boolean),
  );
  if (shipmentResourceIds.size === 0) {
    return;
  }
  const allChecklists = await listResetItems(
    listIdQueries.listChecklistIds,
    "listChecklists",
    reloadYardId,
  );
  const checklistRows = allChecklists.filter(
    (c) => c.resourceId && shipmentResourceIds.has(c.resourceId),
  );
  await runResetMutationBatch(
    "Delete Checklists (inbound/outbound shipment-linked)",
    checklistRows,
    buildDeleteByIdOperation(idOnlyMutations.deleteIdOnly("Checklist")),
  );
};

const buildDeleteByIdOperation = (mutation) => (item) =>
  graphqlOperation(mutation, { input: { id: item.id } });

// One page of a listing, which a bad row cannot sink. A stored row missing a
// non-nullable field the query selects comes back as a null item, and Amplify
// then rejects the whole page — that is how one broken shipment used to stop a
// reset cold. The page is still on the rejection, so it is kept; and the rows
// that came back null are named by reading the same page again with only { id }
// selected. Same filter, same limit, same token: the same scan window.
const fetchListPage = async (query, variables, responseKey) => {
  let response;
  try {
    response = await API.graphql(graphqlOperation(query, variables));
  } catch (err) {
    if (!err?.data?.[responseKey]) {
      throw err;
    }
    response = err;
  }

  const page = response.data[responseKey];
  const items = page.items || [];
  const nextToken = page.nextToken || null;
  if (items.every(Boolean)) {
    return { items, nextToken };
  }

  const idOnlyQuery = query.replace(/items\s*\{[^}]*\}/, "items { id }");
  const idPage = (await API.graphql(graphqlOperation(idOnlyQuery, variables)))
    .data[responseKey];
  const listed = new Set(items.filter(Boolean).map((item) => item.id));
  const recovered = (idPage.items || []).filter(
    (item) => item && !listed.has(item.id),
  );
  console.log(
    `${responseKey}: ${recovered.length} row(s) with missing required fields, listed by id only`,
    recovered.map((item) => item.id),
  );

  return { items: [...items.filter(Boolean), ...recovered], nextToken };
};

const listResetItems = async (query, responseKey, reloadYardId) => {
  const items = [];
  let nextToken = null;

  do {
    const page = await fetchListPage(
      query,
      buildResetListVariables(responseKey, reloadYardId, nextToken),
      responseKey,
    );
    items.push(...page.items);
    nextToken = page.nextToken;
  } while (nextToken);

  return items;
};

// Every row of a table, no yard filter. Used where the reset has to reach rows
// the per-yard listing cannot see — a reloadYardId pointing at a yard that no
// longer exists, or a model that carries no reloadYardId at all.
const listUnfilteredItems = async (query, responseKey) => {
  const items = [];
  let nextToken = null;

  do {
    const page = await fetchListPage(
      query,
      { limit: RESET_PAGE_LIMIT, nextToken },
      responseKey,
    );
    items.push(...page.items);
    nextToken = page.nextToken;
  } while (nextToken);

  return items;
};

// A rejected write whose response still names the row was carried out. AppSync
// runs the resolver first and serializes the response after, so a selected
// connection that points at a broken row errors the response of a write that
// already happened. The root field is nullable, so the error stops below it and
// the id survives. runPromisesInConcurrencyChunks hands back its retry
// wrapper, with the response under `cause`.
const wroteRow = (reason) =>
  Object.values((reason?.cause || reason)?.data || {}).some((root) => root?.id);

// A delete refused because the row is already gone. An id-only delete carries
// no condition of its own, so on a model without @auth the resolver's
// attribute_exists(id) is the only check that can fail.
const rowAlreadyGone = (reason) =>
  isConditionalCheckFailed(reason) || isConditionalCheckFailed(reason?.cause);

// `succeeded` decides which rejections still count as done. Most callers list
// the rows they write moments before, so only wroteRow applies; a caller that
// can re-send a delete for a row an earlier run removed passes its own.
const runResetMutationBatch = async (
  label,
  items,
  createOperation,
  getItemId = (item) => item.id,
  succeeded = wroteRow,
) => {
  console.log(`${label}: `, items.length);

  if (!items.length) {
    return;
  }

  const results = await runPromisesInConcurrencyChunks(
    items.map((item) => () => API.graphql(createOperation(item))),
  );

  const failedResults = results
    .map((result, index) => {
      if (result.status === "rejected" && !succeeded(result.reason)) {
        return {
          id: getItemId(items[index]),
          error: flattenGraphqlError(result.reason),
        };
      }
      return null;
    })
    .filter(Boolean);

  if (failedResults.length > 0) {
    const error = new Error(
      `${label}: Failed ${failedResults.length} of ${items.length}`,
    );
    error.failedResults = failedResults;
    throw error;
  }
};

const runResetListMutation = async ({
  label,
  query,
  responseKey,
  reloadYardId,
  createOperation,
  getItemId,
  // Optional predicate applied after listing. Used by the carrierApp reset,
  // where most shipments never referenced a carrier and mutating them would be
  // thousands of writes for no change.
  filterItems,
}) => {
  const listed = await listResetItems(query, responseKey, reloadYardId);
  const items = filterItems ? listed.filter(filterItems) : listed;
  await runResetMutationBatch(label, items, createOperation, getItemId);
};

// Runs every operation even when one fails, then reports them all: a table the
// reset cannot finish is no reason to leave the next one untouched.
const runResetOperations = async (reloadYardId, operations) => {
  const errors = [];
  for (const operation of operations) {
    try {
      await runResetListMutation({ reloadYardId, ...operation });
    } catch (err) {
      console.log(`${operation.label}: FAILED`, err);
      errors.push(err);
    }
  }

  if (errors.length === 1) {
    throw errors[0];
  }
  if (errors.length > 1) {
    const error = new Error(
      errors.map((err) => flattenGraphqlError(err)).join(" | "),
    );
    error.failedResults = errors.flatMap((err) => err.failedResults || []);
    throw error;
  }
};

const runResetPhase = async (
  phaseId,
  phaseLabel,
  phaseAction,
  notify,
  resetName = "resetYardData",
) => {
  try {
    console.log(phaseLabel);
    await phaseAction();
    return true;
  } catch (err) {
    logError(`admin:utils:${resetName}:${phaseId}`, err, false);
    notify(`Halted during ${phaseLabel}. Check logs for failed ids.`, "error");
    return false;
  }
};

// Runs every phase, one after the other, even when one fails: rows the reset
// cannot delete — usually old rows missing fields the schema now requires — are
// no reason to leave the rest of the yard in place. Each failure is logged with
// its failed ids. Returns the labels of the phases that failed.
const runAllPhases = async (phases, resetName) => {
  const failedPhases = [];

  for (const [index, phase] of phases.entries()) {
    const label = `Phase ${index + 1}: ${phase.label}`;
    try {
      console.log(`${label}...`);
      await phase.run();
    } catch (err) {
      logError(`admin:utils:${resetName}:Phase${index + 1}`, err, false);
      console.log(`${label} FAILED — continuing`, err);
      failedPhases.push(label);
    }
  }

  return failedPhases;
};

// The yard row a reset or delete works from. Accepted even when the response
// carries errors: getReloadYard selects connections, and one broken row behind
// them would otherwise keep the yard from ever being reset or deleted.
const fetchReloadYardForReset = async (reloadYardId, notify, resetName) => {
  let reloadYardRecord;
  try {
    const yardResponse = await API.graphql(
      graphqlOperation(queries.getReloadYard, { id: reloadYardId }),
    );
    reloadYardRecord = yardResponse.data.getReloadYard;
  } catch (err) {
    reloadYardRecord = err?.data?.getReloadYard;
    if (!reloadYardRecord) {
      logError(`admin:utils:${resetName}:fetchYard`, err, notify);
      notify("Failed to fetch reload yard", "error");
      return null;
    }
  }

  if (!reloadYardRecord) {
    notify("Reload yard not found", "error");
  }
  return reloadYardRecord;
};

// The gate check-in surface of one yard: the check-in rows and the driver
// sessions that hold them.
//
// Neither is reachable from anything else the resets delete, and nothing else
// deletes them per yard — devResetGate is environment-wide, so a yard-scoped run
// that skipped these left the manager Gate Queue listing check-ins whose
// shipments were gone (gateCheckInByYardByStatus reads the check-in row's own
// denormalized fields, so nothing hid them).
//
// Sessions first: DriverSession.gateCheckInId points AT a check-in, so deleting
// the check-in first would leave the session pointing at nothing for as long as
// the batch takes. Both must go before the shipments they name, which is why
// this belongs in phase 1 of either flow.
const GATE_RESET_OPERATIONS = [
  {
    label: "Delete DriverSessions",
    query: listIdQueries.listDriverSessionIds,
    responseKey: "listDriverSessions",
    createOperation: buildDeleteByIdOperation(
      idOnlyMutations.deleteIdOnly("DriverSession"),
    ),
  },
  {
    label: "Delete GateCheckIns",
    query: listIdQueries.listGateCheckInIds,
    responseKey: "listGateCheckIns",
    createOperation: buildDeleteByIdOperation(
      idOnlyMutations.deleteIdOnly("GateCheckIn"),
    ),
  },
];

// History notes the reset keeps: those logged against the master data it
// preserves. Account, ChargeRule, Product and Service screens write a note under
// the record's own id and their History tabs read it back, so deleting every
// note of the yard left preserved records with their history wiped.
const PRESERVED_HISTORY_PARENT_TYPES = new Set([
  ResourceType.ACCOUNT,
  ResourceType.CHARGE_RULE,
  ResourceType.PRODUCT,
  ResourceType.RELOADYARD,
  ResourceType.SERVICE,
]);

// Lambda calls are heavier than a mutation — each one is a handful of DynamoDB
// writes plus a Rekognition and an S3 delete — so fewer at a time than the
// GraphQL batches use.
const DRIVER_ERASURE_CONCURRENCY = 5;

// Erases what a driver leaves outside AppSync: the CarrierUserIdentity row, the
// face template in the environment's Rekognition collection, and the images
// under driver-identity/ in S3. None of the three is reachable from a mutation,
// so deleting the CarrierUser row through AppSync — which is all a yard delete
// used to do — left the biometrics stored with nothing pointing at them.
//
// /driverAdmin/deleteDriver is the existing per-driver erasure, reused rather
// than reimplemented. It normally leaves a driver alone and only withdraws a
// yard's access — deleting someone's biometrics is not a yard's power — but it
// erases outright when nothing is left holding a claim on the record, and by
// this phase the memberships are already deleted, which is exactly that case.
// The endpoint treats a driver with no memberships as a remnant to finish off
// and deliberately does not consult check-in history there, so a deleted
// check-in the gateCheckInByDriver GSI has not caught up with cannot talk it
// out of the erasure and strand the biometrics.
//
// Never fails the run. The rows this is called for are already deleted, so a
// throw here would report a halt for a delete that finished and send the
// operator looking for a half-deleted yard; the ids that failed are logged
// instead, and calling the endpoint again finishes any of them off.
//
// Chunked with a plain allSettled rather than runPromisesInConcurrencyChunks:
// that helper wraps each task in the GraphQL retry, and safeAPI.post already
// retries a failed REST call itself.
const eraseDriverBiometrics = async (driverUserIds) => {
  if (!driverUserIds.length) {
    console.log("Erase driver biometrics:  0");
    return { erased: 0, failed: 0 };
  }

  const results = [];
  for (let i = 0; i < driverUserIds.length; i += DRIVER_ERASURE_CONCURRENCY) {
    results.push(
      ...(await Promise.allSettled(
        driverUserIds
          .slice(i, i + DRIVER_ERASURE_CONCURRENCY)
          .map((carrierUserId) =>
            safeAPI.post("transREST", "/driverAdmin/deleteDriver", {
              body: { carrierUserId },
            }),
          ),
      )),
    );
  }

  const failed = results
    .map((result, index) =>
      result.status === "rejected"
        ? {
            id: driverUserIds[index],
            error: flattenGraphqlError(result.reason),
          }
        : null,
    )
    .filter(Boolean);

  console.log(
    `Erase driver biometrics: ${driverUserIds.length - failed.length} of ${driverUserIds.length}`,
  );
  if (failed.length) {
    console.log(
      `Driver biometrics NOT erased for ${failed.length} driver(s) — re-run the delete, or use Reset Gate Check-In Data:`,
      failed,
    );
  }

  return {
    erased: driverUserIds.length - failed.length,
    failed: failed.length,
  };
};

// ##############################################################################
// ##############################################################################
// DELETE FUNCTIONS
// ##############################################################################
// ##############################################################################

// Platform accounts that sit in every yard. Their User rows survive a yard
// delete.
const PROTECTED_USER_EMAILS = new Set([
  "admin@transloads.co",
  "admin@chooch.com",
]);

// One yard-scoped model, listed page by page and deleted with an { id }-only
// mutation: the generated delete mutations select connections, and a
// connection pointing at a row an earlier phase deleted fails the response
// even though the row itself went.
const deleteRowsOf = (typeName, query, responseKey, options = {}) => ({
  label: `Delete ${typeName}`,
  query,
  responseKey,
  createOperation: buildDeleteByIdOperation(
    idOnlyMutations.deleteIdOnly(typeName),
  ),
  ...options,
});

// The registration the yard was created from. SetupData has no reloadYardId:
// a row is one person's sign-up, and approval stamps it with the Cognito group
// the yard is then created under. Groups are per yard (YARD_<name>_...), so the
// yard's group finds its registration. A group another yard also holds is left
// out, and so is every unapproved registration, which carries no group at all.
const listYardSetupData = async (reloadYardRecord) => {
  const [registrations, yards] = await Promise.all([
    listUnfilteredItems(listIdQueries.listSetupDataIds, "listSetupDatas"),
    listAllReloadYards(),
  ]);

  const otherYardGroups = new Set(
    yards
      .filter((yard) => yard.id !== reloadYardRecord.id)
      .flatMap((yard) => yard.groups || []),
  );
  const yardGroups = new Set(
    (reloadYardRecord.groups || []).filter(
      (group) => group !== "admin" && !otherYardGroups.has(group),
    ),
  );

  return registrations.filter((row) =>
    (row.groups || []).some((group) => yardGroups.has(group)),
  );
};

// Every object the yard's screens uploaded. fileUtils writes keys under the
// yard id (getDirFileKey, getFileKey), so the yard's prefix in the public level
// holds all of it: shipment, invoice, account and work order attachments, BOL
// and receipt files, templates, logo and map, photos and the File library.
// Driver identity images live under driver-identity/ and go through the
// driverAdmin lambda instead.
//
// Storage.list in this Amplify version returns at most 1000 keys and no token,
// so the prefix is listed again after each pass until nothing is left. A key
// that fails to delete is skipped on the next pass rather than retried forever,
// and fails the phase at the end.
const deleteYardS3Objects = async (reloadYardId) => {
  const prefix = `${reloadYardId}/`;
  const failed = new Map();
  let removed = 0;

  for (;;) {
    const listed = (await Storage.list(prefix, { level: "public" })).filter(
      (item) => item?.key && !failed.has(item.key),
    );
    if (!listed.length) {
      break;
    }

    const results = await runPromisesInConcurrencyChunks(
      listed.map((item) => () => Storage.remove(item.key, { level: "public" })),
    );
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        failed.set(listed[index].key, flattenGraphqlError(result.reason));
      }
    });
    removed += results.filter((result) => result.status === "fulfilled").length;
  }

  console.log(`Delete S3 objects under public/${prefix}: `, removed);

  if (failed.size > 0) {
    const error = new Error(
      `Delete S3 objects: Failed ${failed.size} of ${removed + failed.size}`,
    );
    error.failedResults = [...failed].map(([id, reason]) => ({
      id,
      error: reason,
    }));
    throw error;
  }
};

// Deletes one dev reloadYard and everything that belongs to it.
//
// Each phase lists its rows page by page and reports every row it failed to
// delete. A failed phase does not stop the run — old rows that break the
// schema's rules are logged with their ids and the rest of the yard still goes
// — but it does keep the ReloadYard row: that goes last, and only once every
// phase finished clean, so the yard stays in the dropdown and running the delete
// again picks up what was left.
//
// DELETES:
//   - Every yard-scoped row of every model: shipments and their line items,
//     inventory, work orders, invoices, timeslots, track cars, gate check-ins
//     and driver sessions, seals, barcodes, photos, files, history notes, logs,
//     tasks, audits, raw data, support requests; accounts, products, services,
//     charge rules, payment terms, saved addresses, checklists, tags, API keys,
//     Zapier hooks and actions, yard announcements; areas, zones, aisles,
//     locations, asset and event types, the yard's own rail and truck equipment
//     types, commodities and capabilities; users (except the platform admin
//     accounts) and the yard's carriers
//   - Membership rows (CustomerReloadYardAccount, CarrierUserReloadYardCarrier)
//     and the Customers and CarrierUsers left with no membership in any yard;
//     a person still active in another yard is kept
//   - Driver biometrics of the deleted drivers (CarrierUserIdentity, face
//     template, identity images), through /driverAdmin/deleteDriver
//   - The yard's registration (SetupData), matched by the yard's group
//   - Every S3 object under public/<reloadYardId>/
//   - The ReloadYard row itself
//
// SETTLES AFTERWARDS: the CarrierCompany each deleted Carrier named, with its
// identifier claims, when no other yard still enters it. The handleCarrierChange
// stream takes a company down with its last entry, a moment after the delete.
//
// NOT COVERED — logged at the end for manual cleanup:
//   - Cognito logins of the deleted users and customers. No endpoint deletes a
//     staff or customer Cognito account; a person who signs in again lands with
//     no yard.
//   - The yard's Stripe subscription.
//
// Hard-gated to dev + ADMIN.
export const deleteYardData = async (reloadYardId, notify) => {
  console.clear();

  // Validate user is ADMIN
  if (getEnv() !== "dev" || window.appUser?.appType !== AppType.ADMIN) {
    notify("INVALID YARD DELETE ATTEMPT - NOT ADMIN", "error");
    return false;
  }

  const reloadYardRecord = await fetchReloadYardForReset(
    reloadYardId,
    notify,
    "deleteYardData",
  );
  if (!reloadYardRecord) {
    return false;
  }

  console.log(
    `Starting deletion of yard: ${reloadYardRecord.name} (${reloadYardId})`,
  );

  // Carried from the phase that learns them to the phases that need them.
  let deletedCarrierJunctions = {
    carrierUserIds: [],
    driverUserIds: [],
    junctionIds: [],
  };
  let deletedCarrierUserIds = [];
  let deletedCustomerIds = [];
  let deletedUserEmails = [];

  const phases = [
    {
      // Gate check-ins and driver sessions first: they name the shipments the
      // later phases delete, and the driver erasure needs the check-ins already
      // gone to delete each driver outright rather than leave an anonymized
      // stub behind in a yard that is going away.
      label: "Deleting leaf entities",
      run: () =>
        runResetOperations(reloadYardId, [
          ...GATE_RESET_OPERATIONS,
          deleteRowsOf(
            "ZapierHook",
            listIdQueries.listZapierHookIds,
            "listZapierHooks",
          ),
          deleteRowsOf(
            "ZapierAction",
            listIdQueries.listZapierActionIds,
            "listZapierActions",
          ),
          deleteRowsOf(
            "WorkChargeRule",
            listIdQueries.listWorkChargeRuleIds,
            "listWorkChargeRules",
          ),
          deleteRowsOf("Barcode", listIdQueries.listBarcodeIds, "listBarcodes"),
          // LocationTag before Tag, so no join row points at a deleted tag.
          deleteRowsOf(
            "LocationTag",
            listIdQueries.listLocationTagIds,
            "listLocationTags",
          ),
          deleteRowsOf("Tag", listIdQueries.listTagIds, "listTags"),
          deleteRowsOf("Task", listIdQueries.listTaskIds, "listTasks"),
          deleteRowsOf("Audit", listIdQueries.listAuditIds, "listAudits"),
          deleteRowsOf("Log", listIdQueries.listLogIds, "listLogs"),
          deleteRowsOf("File", listIdQueries.listFileIds, "listFiles"),
          deleteRowsOf(
            "Announcement",
            listIdQueries.listAnnouncementIds,
            "listAnnouncements",
          ),
          deleteRowsOf(
            "ChargeRule",
            listIdQueries.listChargeRuleIds,
            "listChargeRules",
          ),
          deleteRowsOf("ApiKey", listIdQueries.listApiKeyIds, "listApiKeys"),
          deleteRowsOf(
            "Timeslot",
            listIdQueries.listTimeslotIds,
            "listTimeslots",
          ),
          deleteRowsOf(
            "PaymentTerm",
            listIdQueries.listPaymentTermIds,
            "listPaymentTerms",
          ),
          deleteRowsOf("Seal", listIdQueries.listSealIds, "listSeals"),
          deleteRowsOf(
            "TrackCar",
            listIdQueries.listTrackCarIds,
            "listTrackCars",
          ),
          deleteRowsOf("Photo", listIdQueries.listPhotoIds, "listPhotos"),
          deleteRowsOf("RawData", listIdQueries.listRawDataIds, "listRawDatas"),
          deleteRowsOf(
            "HistoryNote",
            listIdQueries.listHistoryNoteIds,
            "listHistoryNotes",
          ),
          deleteRowsOf(
            "SupportRequest",
            listIdQueries.listSupportRequestIds,
            "listSupportRequests",
          ),
        ]),
    },
    {
      label: "Deleting shipment line item inventory items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ShipmentLineItemInventoryItem",
            listIdQueries.listShipmentLineItemInventoryItemIds,
            "listShipmentLineItemInventoryItems",
          ),
        ]),
    },
    {
      label: "Deleting shipment line items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ShipmentLineItem",
            listIdQueries.listShipmentLineItemIds,
            "listShipmentLineItems",
          ),
        ]),
    },
    {
      label: "Deleting outbound shipments",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "OutboundShipment",
            listIdQueries.listOutboundShipmentIds,
            "listOutboundShipments",
          ),
        ]),
    },
    {
      // Before inventory: work orders point at input and output inventory.
      label: "Deleting work order items and work orders",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "WorkOrderItem",
            listIdQueries.listWorkOrderItemIds,
            "listWorkOrderItems",
          ),
          deleteRowsOf(
            "WorkOrder",
            listIdQueries.listWorkOrderIds,
            "listWorkOrders",
          ),
        ]),
    },
    {
      label: "Deleting inventory items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InventoryItem",
            listIdQueries.listInventoryItemIds,
            "listInventoryItems",
          ),
        ]),
    },
    {
      label: "Deleting inventories",
      run: () =>
        runResetOperations(reloadYardId, [
          // Not deleteRowsOf: the transformer named this one deleteInventorie.
          {
            label: "Delete Inventory",
            query: listIdQueries.listInventoryIds,
            responseKey: "listInventories",
            createOperation: buildDeleteByIdOperation(
              idOnlyMutations.deleteInventorieIdOnly,
            ),
          },
        ]),
    },
    {
      label: "Deleting invoice services and invoices",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InvoiceService",
            listIdQueries.listInvoiceServiceIds,
            "listInvoiceServices",
          ),
          deleteRowsOf("Invoice", listIdQueries.listInvoiceIds, "listInvoices"),
        ]),
    },
    {
      label: "Deleting inbound shipments",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InboundShipment",
            listIdQueries.listInboundShipmentIds,
            "listInboundShipments",
          ),
        ]),
    },
    {
      label: "Deleting products and saved addresses",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ProductStats",
            listIdQueries.listProductStatsIds,
            "listProductStatss",
          ),
          deleteRowsOf("Product", listIdQueries.listProductIds, "listProducts"),
          deleteRowsOf(
            "SavedAddress",
            listIdQueries.listSavedAddressIds,
            "listSavedAddresss",
          ),
        ]),
    },
    {
      label: "Deleting services",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf("Service", listIdQueries.listServiceIds, "listServices"),
        ]),
    },
    {
      // Customer has no reloadYardId: the yard reaches a customer only through
      // the membership rows deleted here, so the people they named are checked
      // in the same phase.
      //
      // Customers go first, memberships after. The memberships are the only
      // record of which customers this yard named; deleted first, a customer
      // step that then failed would leave the re-run with nothing to find them
      // by. The remaining-membership check already ignores this yard's rows, so
      // they need not be gone for it.
      label: "Deleting customers and customer memberships",
      run: async () => {
        const memberships = await listResetItems(
          listIdQueries.listCustomerReloadYardAccountIds,
          "listCustomerReloadYardAccounts",
          reloadYardId,
        );
        deletedCustomerIds = await deleteUnreferencedCustomers(
          [
            ...new Set(
              memberships.map((item) => item.customerId).filter(Boolean),
            ),
          ],
          new Set(memberships.map((item) => item.id)),
        );
        await runResetMutationBatch(
          "Delete CustomerReloadYardAccounts",
          memberships,
          buildDeleteByIdOperation(
            idOnlyMutations.deleteIdOnly("CustomerReloadYardAccount"),
          ),
        );
      },
    },
    {
      // Memberships are deleted rather than retired, which everywhere else
      // would be wrong: a retired row is what stops the colleague cascade
      // re-granting access a yard withdrew (see planColleagueGrants in the
      // handleCarrierUserChange Lambda). It is safe here only because the
      // Carrier entries go with them, leaving no entry for a cascade to grant
      // into.
      label: "Deleting carrier memberships and carriers",
      run: async () => {
        deletedCarrierJunctions =
          await deleteCarrierJunctionsForReset(reloadYardId);
        await runResetOperations(reloadYardId, [
          {
            label: "Delete Carriers",
            query: listIdQueries.listCarrierIds,
            responseKey: "listCarriers",
            createOperation: buildDeleteByIdOperation(
              idOnlyMutations.deleteCarrierIdOnly,
            ),
          },
        ]);
      },
    },
    {
      // CarrierUser has no reloadYardId, so the people are reached through the
      // memberships the previous phase deleted — and only those left with no
      // membership in any other yard go.
      label: "Deleting carrier users",
      run: async () => {
        deletedCarrierUserIds = await deleteUnreferencedCarrierUsers(
          deletedCarrierJunctions.carrierUserIds,
          new Set(deletedCarrierJunctions.junctionIds),
        );
      },
    },
    {
      // What the deleted drivers left outside AppSync. Only the ones the
      // previous phase deleted: a driver kept because they still work another
      // yard keeps their enrollment. Never fails the run — see
      // eraseDriverBiometrics.
      label: "Erasing driver biometrics",
      run: async () => {
        const deletedIds = new Set(deletedCarrierUserIds);
        await eraseDriverBiometrics(
          deletedCarrierJunctions.driverUserIds.filter((id) =>
            deletedIds.has(id),
          ),
        );
      },
    },
    {
      label: "Deleting accounts",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf("Account", listIdQueries.listAccountIds, "listAccounts"),
        ]),
    },
    {
      label: "Deleting locations, aisles, zones and areas",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "Location",
            listIdQueries.listLocationIds,
            "listLocations",
          ),
          deleteRowsOf("Aisle", listIdQueries.listAisleIds, "listAisles"),
          deleteRowsOf("Zone", listIdQueries.listZoneIds, "listZones"),
          deleteRowsOf("Area", listIdQueries.listAreaIds, "listAreas"),
        ]),
    },
    {
      // Only the yard's own equipment types: rows with no reloadYardId are the
      // platform defaults every yard shares, and the yard filter never lists
      // them.
      label: "Deleting event, asset and equipment types",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "EventType",
            listIdQueries.listEventTypeIds,
            "listEventTypes",
          ),
          deleteRowsOf(
            "AssetType",
            listIdQueries.listAssetTypeIds,
            "listAssetTypes",
          ),
          deleteRowsOf(
            "RailEquipmentType",
            listIdQueries.listRailEquipmentTypeIds,
            "listRailEquipmentTypes",
          ),
          deleteRowsOf(
            "TruckEquipmentType",
            listIdQueries.listTruckEquipmentTypeIds,
            "listTruckEquipmentTypes",
          ),
        ]),
    },
    {
      label: "Deleting checklists, commodities and capabilities",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "Checklist",
            listIdQueries.listChecklistIds,
            "listChecklists",
          ),
          deleteRowsOf(
            "ReloadYardCommodity",
            listIdQueries.listReloadYardCommodityIds,
            "listReloadYardCommoditys",
          ),
          deleteRowsOf(
            "ReloadYardCapability",
            listIdQueries.listReloadYardCapabilityIds,
            "listReloadYardCapabilitys",
          ),
        ]),
    },
    {
      label: "Deleting the yard registration and users",
      run: async () => {
        await runResetMutationBatch(
          "Delete SetupData",
          await listYardSetupData(reloadYardRecord),
          buildDeleteByIdOperation(idOnlyMutations.deleteIdOnly("SetupData")),
        );

        const users = (
          await listResetItems(
            listIdQueries.listUserIds,
            "listUsers",
            reloadYardId,
          )
        ).filter((item) => !PROTECTED_USER_EMAILS.has(item.email));
        await runResetMutationBatch(
          "Delete Users",
          users,
          buildDeleteByIdOperation(idOnlyMutations.deleteIdOnly("User")),
        );
        deletedUserEmails = users.map((item) => item.email);
      },
    },
    {
      label: "Deleting S3 objects",
      run: () => deleteYardS3Objects(reloadYardId),
    },
  ];

  const failedPhases = await runAllPhases(phases, "deleteYardData");
  if (failedPhases.length) {
    console.log(
      `DELETE INCOMPLETE, yard row kept: ${reloadYardRecord.name}  ${reloadYardId}`,
      failedPhases,
    );
    notify(
      `Delete incomplete — ${failedPhases.length} phase(s) left rows behind: ${failedPhases.join("; ")}. The yard is kept so the delete can be run again; check logs for failed ids.`,
      "error",
    );
    return false;
  }

  try {
    console.log("Deleting the reload yard...");
    await runResetMutationBatch(
      "Delete ReloadYard",
      [{ id: reloadYardId }],
      buildDeleteByIdOperation(idOnlyMutations.deleteIdOnly("ReloadYard")),
    );
  } catch (err) {
    logError("admin:utils:deleteYardData:deleteReloadYard", err, false);
    notify(
      "Everything else is gone, but the yard row did not delete.",
      "error",
    );
    return false;
  }

  if (deletedUserEmails.length || deletedCustomerIds.length) {
    console.log(
      "Cognito logins NOT deleted — remove them in the Cognito console:",
      { users: deletedUserEmails, customerIds: deletedCustomerIds },
    );
  }
  if (reloadYardRecord.subscription?.subscriptionId) {
    console.log(
      `Stripe subscription NOT cancelled: ${reloadYardRecord.subscription.subscriptionId} (customer ${reloadYardRecord.subscription.customerId})`,
    );
  }

  console.log(`ALL DONE DELETED: ${reloadYardRecord.name}  ${reloadYardId}`);
  notify("Yard deleted successfully.", "info");
  return true;
};

// Reset a dev reloadYard to a clean operational state for release testing.
//
// Narrow scope: just the operational lifecycle (inbound/outbound/inventory/
// trackcar/timeslot/invoice) plus their FK-closure leaves. Setup/master data
// and yard structure stay in place.
//
// DELETES (operational / transactional data for the given reloadYardId):
//   - LocationTags (BEFORE Tags, so preserved Locations don't end up with
//     dangling tag-connection rows pointing at deleted Tag IDs)
//   - Tags, Tasks, Audits, Logs, Photos, RawData
//     (yard-wide leaves; in this codebase they attach to inbound/outbound/
//     inventory/trackcar — all of which are also being deleted)
//   - HistoryNotes of operational records (shipments, inventory, invoices,
//     tasks); notes on preserved master data stay — see
//     PRESERVED_HISTORY_PARENT_TYPES
//   - Seals (referenceId names an inbound, outbound or inventory item)
//   - WorkChargeRules (manual charges on shipments; invoiceServiceId points at
//     the invoice services deleted in phase 7)
//   - GateCheckIns and DriverSessions of the yard (they name the inbound and
//     outbound rows below; left behind, the manager Gate Queue keeps listing
//     check-ins whose shipments are gone). The DRIVERS themselves and their
//     biometrics are NOT touched — that is environment-wide work and belongs to
//     Reset Gate Check-In Data / Reset Carrier App Data
//   - Checklists whose resourceId is an inbound or outbound shipment id
//     (shipment-scoped checklist instances; Files are not deleted on reset)
//   - Barcodes (FK into inboundShipmentId / inventoryId / inventoryItemId;
//     would orphan into broken scanner workflows otherwise)
//   - Timeslots (ALL: booked + schedule templates)
//   - TrackCars (hard-deleted for a truly clean dev/test yard)
//   - ShipmentLineItemInventoryItems, ShipmentLineItems (FK closure)
//   - OutboundShipments
//   - WorkOrderItems, WorkOrders (FK into inventory / inventory items)
//   - InventoryItems, Inventories
//   - InvoiceServices, Invoices
//   - InboundShipments
//   - ProductStats
//   - SupportRequests
//
// PRESERVES (everything else):
//   - Drivers (CarrierUser), their CarrierUserIdentity rows, face templates and
//     stored images: a driver is not the yard's record, and this reset is one
//     yard's operational data
//   - ReloadYard itself + all setup/master data: Accounts, Users, Products,
//     Services, ChargeRules, PaymentTerms, Carriers (+ CarrierUsers/Tokens),
//     Customers (+ CustomerReloadYardAccounts), SavedAddresses, Files,
//     AssetTypes, EventTypes, non-shipment Checklists, ApiKeys,
//     Announcements, Zapier* SetupDatas, Locations/Aisles/Zones/
//     Areas (rows preserved)
//   - HistoryNotes on Accounts, ChargeRules, Products, Services, ReloadYard
//   - ReloadYard counters (invoiceCounter, bolCounter, receiptCounter) — keep
//     incrementing across reset; document numbering will not restart at 1
//
// RESETS (runtime / denormalized fields only, structures preserved):
//   - Product: totalQuantityOnHand / totalQuantityCommitted -> 0;
//     lowInventoryAlert recalculated from the zero-quantity state;
//   - ProductStats: rows deleted so velocity stats recalculate from a clean slate
//   - Location: capacityUsed -> 0; capacityAllocated -> 0; productId,
//     productName, bulkCapacity, bulkUnits, containerNumber, accountName -> null
//     (locationLoad is preserved — load-type selection / structure for the slot)
//   - Aisle: capacityUsed -> 0 (locationLoad preserved; capacityUsed is
//     maintained by per-mutation client-side deltas on the placement paths
//     (promiseSetCapacityUsed callers); clearing it here avoids stale
//     utilization after shipments are gone)
//
// Runs every phase even when one fails, and reports the failed phases at the
// end: old rows that break the schema's rules (a required field missing, a link
// to a row that is gone) are logged with their ids instead of stopping the
// reset. Every write selects only { id } for the same reason.
//
// Hard-gated to dev + ADMIN. Mirrors the phase ordering of deleteYardData for
// foreign-key safety.
export const resetYardData = async (reloadYardId, notify) => {
  console.clear();

  if (getEnv() !== "dev" || window.appUser?.appType !== AppType.ADMIN) {
    notify("INVALID YARD RESET ATTEMPT - NOT ADMIN", "error");
    return false;
  }

  const reloadYardRecord = await fetchReloadYardForReset(
    reloadYardId,
    notify,
    "resetYardData",
  );
  if (!reloadYardRecord) {
    return false;
  }

  console.log(
    `Starting RESET of yard: ${reloadYardRecord.name} (${reloadYardId})`,
  );

  const phases = [
    {
      // Gate check-ins and driver sessions lead, because they name the inbound
      // and outbound rows later phases delete — see GATE_RESET_OPERATIONS.
      // LocationTag before Tag, so no preserved Location keeps a tag-connection
      // row pointing at a deleted Tag. Barcodes point at inbound shipments,
      // inventories and inventory items, and would resolve to deleted rows in
      // scanner workflows if left behind.
      label: "Deleting operational leaf entities",
      run: () =>
        runResetOperations(reloadYardId, [
          ...GATE_RESET_OPERATIONS,
          deleteRowsOf(
            "LocationTag",
            listIdQueries.listLocationTagIds,
            "listLocationTags",
          ),
          deleteRowsOf("Tag", listIdQueries.listTagIds, "listTags"),
          deleteRowsOf("Task", listIdQueries.listTaskIds, "listTasks"),
          deleteRowsOf("Audit", listIdQueries.listAuditIds, "listAudits"),
          deleteRowsOf("Log", listIdQueries.listLogIds, "listLogs"),
          deleteRowsOf(
            "HistoryNote",
            listIdQueries.listHistoryNoteIds,
            "listHistoryNotes",
            {
              label: "Delete HistoryNote (operational)",
              filterItems: (item) =>
                !PRESERVED_HISTORY_PARENT_TYPES.has(item.parentType),
            },
          ),
          deleteRowsOf("Seal", listIdQueries.listSealIds, "listSeals"),
          deleteRowsOf(
            "WorkChargeRule",
            listIdQueries.listWorkChargeRuleIds,
            "listWorkChargeRules",
          ),
          deleteRowsOf("Photo", listIdQueries.listPhotoIds, "listPhotos"),
          deleteRowsOf("RawData", listIdQueries.listRawDataIds, "listRawDatas"),
          deleteRowsOf("Barcode", listIdQueries.listBarcodeIds, "listBarcodes"),
          deleteRowsOf(
            "Timeslot",
            listIdQueries.listTimeslotIds,
            "listTimeslots",
          ),
          deleteRowsOf(
            "TrackCar",
            listIdQueries.listTrackCarIds,
            "listTrackCars",
          ),
          deleteRowsOf(
            "SupportRequest",
            listIdQueries.listSupportRequestIds,
            "listSupportRequests",
          ),
        ]),
    },
    {
      label: "Deleting checklists linked to inbound/outbound shipments",
      run: () => deleteShipmentLinkedChecklistsForReset(reloadYardId),
    },
    {
      label: "Deleting shipment line item inventory items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ShipmentLineItemInventoryItem",
            listIdQueries.listShipmentLineItemInventoryItemIds,
            "listShipmentLineItemInventoryItems",
          ),
        ]),
    },
    {
      label: "Deleting shipment line items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ShipmentLineItem",
            listIdQueries.listShipmentLineItemIds,
            "listShipmentLineItems",
          ),
        ]),
    },
    {
      label: "Deleting outbound shipments",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "OutboundShipment",
            listIdQueries.listOutboundShipmentIds,
            "listOutboundShipments",
          ),
        ]),
    },
    {
      // Before inventory: work orders point at input and output inventory.
      label: "Deleting work order items and work orders",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "WorkOrderItem",
            listIdQueries.listWorkOrderItemIds,
            "listWorkOrderItems",
          ),
          deleteRowsOf(
            "WorkOrder",
            listIdQueries.listWorkOrderIds,
            "listWorkOrders",
          ),
        ]),
    },
    {
      label: "Deleting inventory items",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InventoryItem",
            listIdQueries.listInventoryItemIds,
            "listInventoryItems",
          ),
        ]),
    },
    {
      label: "Deleting inventories",
      run: () =>
        runResetOperations(reloadYardId, [
          // Not deleteRowsOf: the transformer named this one deleteInventorie.
          {
            label: "Delete Inventory",
            query: listIdQueries.listInventoryIds,
            responseKey: "listInventories",
            createOperation: buildDeleteByIdOperation(
              idOnlyMutations.deleteInventorieIdOnly,
            ),
          },
        ]),
    },
    {
      label: "Deleting invoice services and invoices",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InvoiceService",
            listIdQueries.listInvoiceServiceIds,
            "listInvoiceServices",
          ),
          deleteRowsOf("Invoice", listIdQueries.listInvoiceIds, "listInvoices"),
        ]),
    },
    {
      label: "Deleting inbound shipments",
      run: () =>
        runResetOperations(reloadYardId, [
          deleteRowsOf(
            "InboundShipment",
            listIdQueries.listInboundShipmentIds,
            "listInboundShipments",
          ),
        ]),
    },
    {
      label: "Deleting ProductStats and resetting Product derived fields",
      run: async () => {
        await runResetOperations(reloadYardId, [
          deleteRowsOf(
            "ProductStats",
            listIdQueries.listProductStatsIds,
            "listProductStatss",
          ),
        ]);
        await resetProductDerivedData(reloadYardId);
      },
    },
    {
      label: "Resetting Location and Aisle runtime fields",
      run: () =>
        runResetOperations(reloadYardId, [
          {
            label: "Reset Locations (runtime fields)",
            query: listIdQueries.listLocationIds,
            responseKey: "listLocations",
            createOperation: (item) =>
              graphqlOperation(idOnlyMutations.updateIdOnly("Location"), {
                input: {
                  id: item.id,
                  // Required when mutating `sortKey` / `productId` — they participate in
                  // composite @key entries on `Location` (e.g. `locationByProductBySortKey`,
                  // `locationByAisleByActiveBySortKey`).
                  reloadYardId: item.reloadYardId,
                  areaId: item.areaId,
                  zoneId: item.zoneId,
                  aisleId: item.aisleId,
                  active: item.active,
                  sortKey: item.sortKey,
                  capacityUsed: 0,
                  capacityAllocated: 0,
                  productId: null,
                  productName: null,
                  bulkCapacity: null,
                  bulkUnits: null,
                  containerNumber: null,
                  accountName: null,
                  isContaminated: false,
                },
              }),
          },
          {
            label: "Reset Aisles (capacityUsed)",
            query: listIdQueries.listAisleIds,
            responseKey: "listAisles",
            createOperation: (item) =>
              graphqlOperation(idOnlyMutations.updateIdOnly("Aisle"), {
                input: { id: item.id, capacityUsed: 0 },
              }),
          },
        ]),
    },
  ];

  const failedPhases = await runAllPhases(phases, "resetYardData");
  if (failedPhases.length) {
    console.log(
      `RESET FINISHED WITH ERRORS: ${reloadYardRecord.name}  ${reloadYardId}`,
      failedPhases,
    );
    notify(
      `Reset finished, but ${failedPhases.length} phase(s) left rows behind: ${failedPhases.join("; ")}. Check logs for failed ids.`,
      "warning",
    );
    return false;
  }

  console.log(`ALL DONE RESET: ${reloadYardRecord.name}  ${reloadYardId}`);
  notify("Yard reset successfully.", "info");
  return true;
};

// ##############################################################################
// CARRIER APP RESET
// ##############################################################################

const hasCarrierReference = (item) =>
  Boolean(item?.carrierId || item?.carrierName || item?.carrier);

const hasCarrierOrDriverReference = (item) =>
  hasCarrierReference(item) || Boolean(item?.driverId || item?.driverName);

const hasTimeslotCarrierReference = (item) =>
  Boolean(item?.carrierId || item?.carrierUserId);

// Deletes the yard's membership rows and reports which carrier users they
// belonged to, so the caller can decide which of those users are now unreachable
// from any yard. The junction ids go back too: the orphan check reads a GSI that
// still serves the rows deleted here, and has to be told to ignore them.
//
// driverUserIds is the subset that were drivers, read off the membership rather
// than the person: CarrierUser.userType is not in the id projection, and the
// membership is the row the yard actually held. Their biometrics live outside
// AppSync and have to be erased through the lambda — see eraseDriverBiometrics.
const deleteCarrierJunctionsForReset = async (reloadYardId) => {
  const junctions = await listResetItems(
    listIdQueries.listCarrierUserReloadYardCarrierIds,
    "listCarrierUserReloadYardCarriers",
    reloadYardId,
  );

  await runResetMutationBatch(
    "Delete CarrierUserReloadYardCarriers",
    junctions,
    buildDeleteByIdOperation(
      idOnlyMutations.deleteCarrierUserReloadYardCarrierIdOnly,
    ),
  );

  return {
    carrierUserIds: [
      ...new Set(junctions.map((item) => item.carrierUserId).filter(Boolean)),
    ],
    driverUserIds: [
      ...new Set(
        junctions
          .filter(
            (item) =>
              normalizeCarrierUserType(item.type) === UserType.CARRIER_DRIVER,
          )
          .map((item) => item.carrierUserId)
          .filter(Boolean),
      ),
    ],
    junctionIds: junctions.map((item) => item.id),
  };
};

const REMAINING_MEMBERSHIP_PAGE_LIMIT = 25;

// True while the user still holds a membership somewhere. Rows this job already
// deleted are ignored: the query reads the reloadYardCarrierByUser GSI, whose
// deletes propagate asynchronously, so the memberships deleted moments ago keep
// coming back for a while — believed, they would make every user look busy and
// no CarrierUser would ever be deleted.
//
// Paged rather than limit-1: skipping the stale rows can empty a page while a
// real membership waits on the next one.
const hasLiveIndexRow = async (query, responseKey, variables, deletedIds) => {
  let nextToken = null;

  do {
    const response = await API.graphql(
      graphqlOperation(query, {
        ...variables,
        limit: REMAINING_MEMBERSHIP_PAGE_LIMIT,
        nextToken,
      }),
    );
    const page = response?.data?.[responseKey];
    const live = (page?.items || [])
      .filter(Boolean)
      .filter((item) => !deletedIds.has(item.id));

    if (live.length) {
      return true;
    }
    nextToken = page?.nextToken || null;
  } while (nextToken);

  return false;
};

const hasRemainingMembership = (carrierUserId, deletedJunctionIds) =>
  hasLiveIndexRow(
    listIdQueries.reloadYardCarrierIdsByUser,
    "reloadYardCarrierByUser",
    { carrierUserId },
    deletedJunctionIds,
  );

// The customer side of the same check, on the reloadYardAccountByCustomer GSI:
// a Customer is a person who may hold accounts in several yards, and only the
// memberships of the yard being deleted are ignored. Those are still in the
// table when this runs (the customer phase deletes them after), so they are
// skipped by id, not by having gone.
const hasRemainingCustomerMembership = (customerId, yardMembershipIds) =>
  hasLiveIndexRow(
    listIdQueries.reloadYardAccountIdsByCustomer,
    "reloadYardAccountByCustomer",
    { customerId },
    yardMembershipIds,
  );

// CarrierUser and Customer have no reloadYardId: a person is reachable from a
// yard only through a membership table, and the same person can hold
// memberships in several dev yards. So the row is deleted only once nothing
// points at it — resetting one yard must not sign someone out of another.
//
// A membership written moments ago may still be missing from the GSI this reads.
// The stale answer costs a leftover person row, which the next reset picks
// up; the alternative failure (deleting a user who still works elsewhere) is the
// one worth avoiding.
//
// Returns the ids actually deleted, which is what the caller needs to know
// whose biometrics to erase: a driver kept because they still work another yard
// must keep their enrollment with them.
const deleteUnreferencedPeople = async ({
  typeName,
  ids,
  hasRemaining,
  mutation,
  deleteSucceeded,
}) => {
  if (!ids.length) {
    console.log(`Delete ${typeName}s:  0`);
    return [];
  }

  const results = await runPromisesInConcurrencyChunks(
    ids.map((id) => () => hasRemaining(id)),
  );

  const failedResults = results
    .map((result, index) =>
      result.status === "rejected"
        ? {
            id: ids[index],
            error: flattenGraphqlError(result.reason),
          }
        : null,
    )
    .filter(Boolean);

  if (failedResults.length > 0) {
    const error = new Error(
      `Check remaining ${typeName} memberships: Failed ${failedResults.length} of ${ids.length}`,
    );
    error.failedResults = failedResults;
    throw error;
  }

  const orphaned = ids
    .filter((_, index) => results[index].value === false)
    .map((id) => ({ id }));

  const kept = ids.length - orphaned.length;
  if (kept > 0) {
    console.log(`Keeping ${kept} ${typeName}(s) still active in other yards`);
  }

  await runResetMutationBatch(
    `Delete ${typeName}s`,
    orphaned,
    buildDeleteByIdOperation(mutation),
    undefined,
    deleteSucceeded,
  );

  return orphaned.map((item) => item.id);
};

const deleteUnreferencedCarrierUsers = (carrierUserIds, deletedJunctionIds) =>
  deleteUnreferencedPeople({
    typeName: "CarrierUser",
    ids: carrierUserIds,
    hasRemaining: (id) => hasRemainingMembership(id, deletedJunctionIds),
    mutation: idOnlyMutations.deleteCarrierUserIdOnly,
  });

// The same rule for customers: a Customer row goes only once the yard delete
// has left it with no account membership in any yard.
//
// A customer already gone counts as deleted. The customer phase deletes
// customers before the memberships that name them, so a re-run after a failed
// membership delete names customers the earlier run removed. Customer has no
// @auth, so a refused delete can only mean the row is missing.
const deleteUnreferencedCustomers = (customerIds, yardMembershipIds) =>
  deleteUnreferencedPeople({
    typeName: "Customer",
    ids: customerIds,
    hasRemaining: (id) => hasRemainingCustomerMembership(id, yardMembershipIds),
    mutation: idOnlyMutations.deleteIdOnly("Customer"),
    deleteSucceeded: (reason) => wroteRow(reason) || rowAlreadyGone(reason),
  });

// Drivers, their sessions, gate check-ins and biometrics belong to the
// driverAdmin lambda: Rekognition and S3 are not reachable from AppSync. That
// endpoint works environment-wide, not per yard — see resetGateCheckInData in
// AdminDashboard, which is the same call on its own button.
const resetGateAndDrivers = async () => {
  const response = await safeAPI.post(
    "transREST",
    "/driverAdmin/devResetGate",
    { body: {} },
  );
  const counts = response?.counts || {};
  console.log("Gate + driver reset:", counts);
  return counts;
};

// Sentinel for the "All Yards" choice in the admin dashboard dropdown. Not a
// yard id, so getReloadYard is never called with it.
export const ALL_RELOAD_YARDS = "ALL_RELOAD_YARDS";

const listAllReloadYards = async () =>
  listUnfilteredItems(listIdQueries.listReloadYardIds, "listReloadYards");

// Environment-wide finish for an All Yards run. Whatever is still in the three
// carrier tables now belongs to no live yard, and the per-yard phases cannot
// reach any of it:
//   - Carriers and memberships whose reloadYardId names a yard that was deleted
//     (the yard is gone from the dropdown, so no run ever lists its rows)
//   - CarrierUsers that never had a membership at all — an abandoned
//     registration or a failed invite writes the person before the junction, and
//     a person with no junction is reachable from no yard
// Archived rows (active "false", deleted true) are ordinary rows to every query
// here: nothing in the reset filters on active or deleted.
const sweepCarrierDataEnvironmentWide = async () => {
  await runResetMutationBatch(
    "Sweep CarrierUserReloadYardCarriers",
    await listUnfilteredItems(
      listIdQueries.listCarrierUserReloadYardCarrierIds,
      "listCarrierUserReloadYardCarriers",
    ),
    buildDeleteByIdOperation(
      idOnlyMutations.deleteCarrierUserReloadYardCarrierIdOnly,
    ),
  );

  await runResetMutationBatch(
    "Sweep Carriers",
    await listUnfilteredItems(listIdQueries.listCarrierIds, "listCarriers"),
    buildDeleteByIdOperation(idOnlyMutations.deleteCarrierIdOnly),
  );

  // Last, and unconditional: with the junction table emptied above, every
  // CarrierUser left is a person no yard can reach.
  await runResetMutationBatch(
    "Sweep CarrierUsers",
    await listUnfilteredItems(
      listIdQueries.listCarrierUserIds,
      "listCarrierUsers",
    ),
    buildDeleteByIdOperation(idOnlyMutations.deleteCarrierUserIdOnly),
  );
};

// The same unreachable rows, counted but not touched, for a single-yard run:
// deleting them would reach outside the yard that was picked. Logged so the rows
// still sitting in the tables afterwards are explained, with the run that does
// remove them named.
//
// Nothing here writes anything, and nothing here may fail the run. It is four
// environment-wide reads that happen after every deleting phase has already
// committed, so a throw let out of it would have runResetPhase report "Reset
// halted" and resetCarrierAppData return false for a reset that finished — the
// operator would go looking for a half-deleted yard that does not exist. A read
// that fails costs the explanation, not the result.
const reportUnreachableCarrierData = async () => {
  try {
    const [yards, junctions, carriers, carrierUsers] = await Promise.all([
      listAllReloadYards(),
      listUnfilteredItems(
        listIdQueries.listCarrierUserReloadYardCarrierIds,
        "listCarrierUserReloadYardCarriers",
      ),
      listUnfilteredItems(listIdQueries.listCarrierIds, "listCarriers"),
      listUnfilteredItems(listIdQueries.listCarrierUserIds, "listCarrierUsers"),
    ]);

    const liveYardIds = new Set(yards.map((yard) => yard.id));
    const usersWithMembership = new Set(
      junctions.map((item) => item.carrierUserId),
    );

    const strandedJunctions = junctions.filter(
      (item) => !liveYardIds.has(item.reloadYardId),
    );
    const strandedCarriers = carriers.filter(
      (item) => !liveYardIds.has(item.reloadYardId),
    );
    const membershiplessUsers = carrierUsers.filter(
      (item) => !usersWithMembership.has(item.id),
    );

    const total =
      strandedJunctions.length +
      strandedCarriers.length +
      membershiplessUsers.length;

    if (!total) {
      return;
    }

    console.log(
      `Left behind (outside the selected yard): ${strandedCarriers.length} Carrier(s) and ${strandedJunctions.length} membership(s) of deleted yards, ${membershiplessUsers.length} CarrierUser(s) with no membership. Run the reset with "All Yards" to delete these.`,
      { strandedCarriers, strandedJunctions, membershiplessUsers },
    );
  } catch (err) {
    logError("admin:utils:resetCarrierAppData:Phase6Report", err, false);
    console.log(
      'Could not check for carrier rows outside the selected yard. The reset itself finished; run it again with "All Yards" to see and clear those rows.',
    );
  }
};

// Everything the company layer holds, read whole. Neither model carries a
// reloadYardId, so there is no per-yard variant of this read: a company belongs
// to every yard that entered it, and a claim to no yard at all.
const listCompanyLayer = async () => {
  const [companies, claims, carriers] = await Promise.all([
    listUnfilteredItems(
      listIdQueries.listCarrierCompanyIds,
      "listCarrierCompanys",
    ),
    listUnfilteredItems(
      listIdQueries.listCarrierIdentifierClaimIds,
      "listCarrierIdentifierClaims",
    ),
    listUnfilteredItems(listIdQueries.listCarrierIds, "listCarriers"),
  ]);

  const stampedCompanyIds = new Set(
    carriers.map((item) => item.companyId).filter(Boolean),
  );
  const liveCompanyIds = new Set(companies.map((item) => item.id));

  return {
    companies,
    claims,
    // A company no Carrier entry names. Nothing can reach it: the client edits
    // a company through its yard entry, and its @auth reads a groups list that
    // was the union of those entries' yards.
    entrylessCompanies: companies.filter(
      (item) => !stampedCompanyIds.has(item.id),
    ),
    // The expensive leftover. A claim is what makes a regulatory number mean
    // anything platform-wide, and one outliving its company keeps that number
    // spoken for by nothing — the next carrier to register it is refused, which
    // is precisely what a carrier reset is supposed to make possible again.
    orphanClaims: claims.filter((item) => !liveCompanyIds.has(item.companyId)),
  };
};

// Deletes the companies no yard entry names, which after phase 6 emptied the
// Carrier table is all of them. The claims are not deleted here and cannot be:
// CarrierIdentifierClaim
// is declared @model(mutations: null), so no delete mutation exists for it — the
// claim is released by handleCarrierChange's teardownCompanyRecord, which the
// company delete below is what triggers. That is a DynamoDB stream, so the
// release lands a moment after this returns; the count logged at the end is read
// straight afterwards and will usually still show them.
const sweepCarrierCompaniesEnvironmentWide = async () => {
  const { entrylessCompanies } = await listCompanyLayer();

  await runResetMutationBatch(
    "Sweep CarrierCompanies",
    entrylessCompanies,
    buildDeleteByIdOperation(idOnlyMutations.deleteCarrierCompanyIdOnly),
  );

  const { companies, claims } = await listCompanyLayer();
  console.log(
    `Company layer after the sweep: ${companies.length} CarrierCompany, ${claims.length} claim(s). ` +
      "Both are torn down by the handleCarrierChange stream and settle a moment " +
      "after this finishes — re-run the reset if either is still standing.",
  );
};

// The same two tables, counted but not touched, for a single-yard run: deleting
// a company would reach outside the yard that was picked, since the company sits
// above the yards and is shared by every one that entered it.
//
// Nothing here writes anything and nothing here may fail the run, for the same
// reason reportUnreachableCarrierData does not: these are environment-wide reads
// that happen after every deleting phase has committed, so a throw would report
// "Reset halted" for a reset that finished.
const reportUnreachableCompanyData = async () => {
  try {
    const { entrylessCompanies, orphanClaims } = await listCompanyLayer();
    if (!entrylessCompanies.length && !orphanClaims.length) {
      return;
    }

    console.log(
      `Company layer left behind: ${entrylessCompanies.length} CarrierCompany with no yard entry, ${orphanClaims.length} claim(s) whose company is gone. ` +
        'Run the reset with "All Yards" to delete the companies; the claims are ' +
        "released by the stream when their company row goes, and cannot be " +
        "deleted directly (CarrierIdentifierClaim has no delete mutation). A " +
        "claim still standing here will refuse that number to the next carrier " +
        "that registers it.",
      { entrylessCompanies, orphanClaims },
    );
  } catch (err) {
    logError("admin:utils:resetCarrierAppData:Phase7Report", err, false);
    console.log(
      "Could not check the carrier company layer. The reset itself finished.",
    );
  }
};

// The yard-scoped half of the carrier reset — everything except the gate and
// driver purge, which is environment-wide and runs once for the whole job.
// Returns false as soon as a phase fails, having already notified.
//
// deletedJunctionIds is the job's running set of membership ids already deleted,
// added to here. It spans yards on purpose: on an All Yards run the GSI can
// still be serving the memberships an earlier yard deleted.
const resetCarrierAppDataForYard = async (
  reloadYardId,
  yardLabel,
  notify,
  deletedJunctionIds,
) => {
  console.log(`Starting CARRIER RESET of yard: ${yardLabel} (${reloadYardId})`);

  // PHASE 2: unhook the shipments and appointments first, so nothing is left
  // pointing at a carrier row that the later phases delete.
  if (
    !(await runResetPhase(
      "Phase2",
      `Phase 2 (${yardLabel}): Clearing carrier/driver references on shipments and timeslots...`,
      async () =>
        runResetOperations(reloadYardId, [
          {
            label: "Clear InboundShipment carrier/driver refs",
            query: listIdQueries.listInboundShipmentCarrierRefs,
            responseKey: "listInboundShipments",
            filterItems: hasCarrierOrDriverReference,
            createOperation: (item) =>
              graphqlOperation(idOnlyMutations.updateInboundShipmentIdOnly, {
                input: {
                  id: item.id,
                  carrierId: null,
                  carrierName: null,
                  driverId: null,
                  driverName: null,
                },
              }),
          },
          {
            label: "Clear OutboundShipment carrier/driver refs",
            query: listIdQueries.listOutboundShipmentCarrierRefs,
            responseKey: "listOutboundShipments",
            filterItems: hasCarrierOrDriverReference,
            createOperation: (item) =>
              graphqlOperation(idOnlyMutations.updateOutboundShipmentIdOnly, {
                input: {
                  id: item.id,
                  // `carrier` is the legacy display-name scalar squatting on the
                  // Carrier connection name — it holds a carrier name, so it
                  // goes with the rest.
                  carrier: null,
                  carrierId: null,
                  carrierName: null,
                  driverId: null,
                  driverName: null,
                },
              }),
          },
          {
            label: "Clear Timeslot carrier refs",
            query: listIdQueries.listTimeslotCarrierRefs,
            responseKey: "listTimeslots",
            filterItems: hasTimeslotCarrierReference,
            createOperation: (item) =>
              graphqlOperation(idOnlyMutations.updateTimeslotIdOnly, {
                input: { id: item.id, carrierId: null, carrierUserId: null },
              }),
          },
        ]),
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  // PHASE 3: the join table, then the people it linked. Order matters: the
  // orphan check in phase 4 reads the memberships this phase deletes.
  let carrierUserIds = [];
  if (
    !(await runResetPhase(
      "Phase3",
      `Phase 3 (${yardLabel}): Deleting carrier memberships...`,
      async () => {
        const deleted = await deleteCarrierJunctionsForReset(reloadYardId);
        carrierUserIds = deleted.carrierUserIds;
        deleted.junctionIds.forEach((id) => deletedJunctionIds.add(id));
      },
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  if (
    !(await runResetPhase(
      "Phase4",
      `Phase 4 (${yardLabel}): Deleting carrier users with no remaining yard...`,
      async () =>
        deleteUnreferencedCarrierUsers(carrierUserIds, deletedJunctionIds),
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  // PHASE 5: the carrier companies themselves, now that nothing links to them.
  if (
    !(await runResetPhase(
      "Phase5",
      `Phase 5 (${yardLabel}): Deleting carriers...`,
      async () =>
        runResetOperations(reloadYardId, [
          {
            label: "Delete Carriers",
            query: listIdQueries.listCarrierIds,
            responseKey: "listCarriers",
            createOperation: buildDeleteByIdOperation(
              idOnlyMutations.deleteCarrierIdOnly,
            ),
          },
        ]),
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  console.log(`DONE CARRIER RESET: ${yardLabel}  ${reloadYardId}`);
  return true;
};

// Full carrierApp reset for a dev reloadYard — or, with ALL_RELOAD_YARDS, for
// every yard in the environment. Puts the yard back to the state it had before
// any carrier ever signed up, so carrier onboarding, driver enrollment and gate
// check-in can be retested end to end.
//
// DELETES:
//   - Carriers of the yard
//   - CarrierUserReloadYardCarrier rows of the yard (the join table)
//   - CarrierUsers left with no membership in any yard (dispatch and drivers
//     alike); a user still active in another yard is kept
//   - On an All Yards run only, everything still in those three tables
//     afterwards: rows whose reloadYardId names a deleted yard, and CarrierUsers
//     that never had a membership. A single-yard run logs their count instead.
//   - On an All Yards run only, CarrierCompany rows no yard entry names. Their
//     CarrierIdentifierClaims go with them, but not from here: the model is
//     declared @model(mutations: null) and has no delete mutation, so the claims
//     are released by handleCarrierChange's teardownCompanyRecord off the
//     company delete — a stream, so a moment later. A single-yard run reports
//     both counts instead.
//   Archived rows go with the rest — no query here filters on active or deleted.
//   - Via /driverAdmin/devResetGate (ENVIRONMENT-WIDE, not yard-scoped, and so
//     run once per job rather than once per yard): every GateCheckIn and
//     DriverSession row, the check-in lock those held on their shipments, every
//     driver CarrierUser with its memberships, every CarrierUserIdentity row,
//     every face in the env's Rekognition collection, and every object under
//     driver-identity/ in S3
//
// CLEARS (rows preserved, carrier/driver references nulled):
//   - InboundShipment: carrierId, carrierName, driverId, driverName
//   - OutboundShipment: carrier, carrierId, carrierName, driverId, driverName
//   - Timeslot: carrierId, carrierUserId
//   Nulling carrierId drops the row out of the carrierApp shipment list index
//   (inboundShipmentByCarrierByStatusByDates / outbound equivalent), which is
//   the point: no carrier should see these loads afterwards. carrierType is
//   left alone — it describes the mode of transport, not a carrier record.
//
// PRESERVES: everything else in the yard, including the shipments, inventory
// and invoices themselves.
//
// NOT COVERED: Cognito accounts. A dispatch user whose CarrierUser row is
// deleted here still has their Cognito login and will land in carrier
// registration on their next sign-in.
//
// Hard-gated to dev + ADMIN, like the other reset flows in this file.
export const resetCarrierAppData = async (reloadYardId, notify) => {
  console.clear();

  if (getEnv() !== "dev" || window.appUser?.appType !== AppType.ADMIN) {
    notify("INVALID CARRIER RESET ATTEMPT - NOT ADMIN", "error");
    return false;
  }

  const allYards = reloadYardId === ALL_RELOAD_YARDS;
  // [yardId, label] pairs. The label is only for logs and phase messages.
  let targets = [];

  try {
    if (allYards) {
      targets = (await listAllReloadYards()).map((yard) => [
        yard.id,
        yard.name,
      ]);
    } else {
      const yardResponse = await API.graphql(
        graphqlOperation(queries.getReloadYard, { id: reloadYardId }),
      );
      const reloadYardRecord = yardResponse.data.getReloadYard;

      if (!reloadYardRecord) {
        notify("Reload yard not found", "error");
        return false;
      }
      targets = [[reloadYardId, reloadYardRecord.name]];
    }
  } catch (err) {
    logError("admin:utils:resetCarrierAppData:fetchYards", err, false);
    notify("Failed to fetch reload yards", "error");
    return false;
  }

  if (!targets.length) {
    notify("No reload yards found", "error");
    return false;
  }

  // PHASE 1: drivers, their sessions, gate check-ins and biometrics. Runs once:
  // the endpoint covers the whole environment whichever yard was picked.
  if (
    !(await runResetPhase(
      "Phase1",
      "Phase 1: Resetting gate check-ins, drivers and biometrics...",
      resetGateAndDrivers,
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  // One yard at a time, and stop at the first that fails: a half-reset yard is
  // easier to reason about than several, and the failed ids are in the log.
  const deletedJunctionIds = new Set();
  for (const [yardId, yardLabel] of targets) {
    if (
      !(await resetCarrierAppDataForYard(
        yardId,
        yardLabel,
        notify,
        deletedJunctionIds,
      ))
    ) {
      return false;
    }
  }

  // PHASE 6: the rows no yard can reach — see sweepCarrierDataEnvironmentWide.
  // Deleted on an All Yards run, only counted on a single-yard one, where they
  // are somebody else's yard's business.
  if (
    !(await runResetPhase(
      "Phase6",
      allYards
        ? "Phase 6: Sweeping carrier rows left with no live yard..."
        : "Phase 6: Checking for carrier rows outside the selected yard...",
      allYards ? sweepCarrierDataEnvironmentWide : reportUnreachableCarrierData,
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  // PHASE 7: the company layer above the yards — CarrierCompany and the
  // regulatory-number claims. Both are maintained by the handleCarrierChange
  // stream, which tears a company down with its last yard entry; this phase is
  // for the entries that teardown declines (a company row that moved under its
  // own delete, an entry deleted before the resolver had stamped it), because a
  // claim left standing refuses that number to the next carrier to register it.
  if (
    !(await runResetPhase(
      "Phase7",
      allYards
        ? "Phase 7: Sweeping carrier companies with no yard entry left..."
        : "Phase 7: Checking the carrier company layer...",
      allYards
        ? sweepCarrierCompaniesEnvironmentWide
        : reportUnreachableCompanyData,
      notify,
      "resetCarrierAppData",
    ))
  ) {
    return false;
  }

  console.log(`ALL DONE CARRIER RESET: ${targets.length} yard(s)`);
  notify(
    allYards
      ? `Carrier app data reset successfully for ${targets.length} yards.`
      : "Carrier app data reset successfully.",
    "info",
  );
  return true;
};

export const deleteAccount = async (accountId, deleteTrue = false, notify) => {
  try {
    debugger;
    const input = { id: accountId };
    try {
      // delete all related tables that depend on the account
      // Checklists by resourceId
      const checklistResponse = await API.graphql(
        graphqlOperation(listIdQueries.listChecklistIds, {
          filter: { resourceId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const checklistItems = checklistResponse.data.listChecklists.items;
      console.log("Delete ChecklistItems: ", checklistItems);
      deleteTrue &&
        (await Promise.allSettled(
          checklistItems.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteChecklist, { input }),
            );
          }),
        ));

      // Invoice Services
      const invSvcRes = await API.graphql(
        graphqlOperation(listIdQueries.listInvoiceServiceIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const invoiceServices = invSvcRes.data.listInvoiceServices.items;
      console.log("Delete InvoiceServices (by account): ", invoiceServices);
      deleteTrue &&
        (await Promise.allSettled(
          invoiceServices.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteInvoiceService, { input }),
            );
          }),
        ));

      // ShipmentLineItemInventoryItems (by ShipmentLineItem)
      const sliRes = await API.graphql(
        graphqlOperation(listIdQueries.listShipmentLineItemIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const shipmentLineItems = sliRes.data.listShipmentLineItems.items;
      console.log(
        "Delete ShipmentLineItemInventoryItems (by SLI): ",
        shipmentLineItems,
      );
      deleteTrue &&
        (await Promise.allSettled(
          shipmentLineItems.map(async (sli) => {
            const sliiRes = await API.graphql(
              graphqlOperation(
                listIdQueries.listShipmentLineItemInventoryItemIds,
                {
                  filter: { shipmentLineItemId: { eq: sli.id } },
                  limit: 1000,
                },
              ),
            );
            const sliis = sliiRes.data.listShipmentLineItemInventoryItems.items;
            return Promise.allSettled(
              sliis.map((item) => {
                const input = { id: item.id };
                return API.graphql(
                  graphqlOperation(
                    mutations.deleteShipmentLineItemInventoryItem,
                    {
                      input,
                    },
                  ),
                );
              }),
            );
          }),
        ));

      // ShipmentLineItems (by account)
      console.log("Delete ShipmentLineItems (by account): ", shipmentLineItems);
      deleteTrue &&
        (await Promise.allSettled(
          shipmentLineItems.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteShipmentLineItem, { input }),
            );
          }),
        ));

      // InventoryItems (by account)
      const invItemRes = await API.graphql(
        graphqlOperation(listIdQueries.listInventoryItemIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const inventoryItems = invItemRes.data.listInventoryItems.items;
      console.log("Delete InventoryItems (by account): ", inventoryItems);
      deleteTrue &&
        (await Promise.allSettled(
          inventoryItems.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteInventoryItem, { input }),
            );
          }),
        ));

      // Inventories (by account)
      const invRes = await API.graphql(
        graphqlOperation(listIdQueries.listInventoryIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const inventories = invRes.data.listInventories.items;
      console.log("Delete Inventories (by account): ", inventories);
      deleteTrue &&
        (await Promise.allSettled(
          inventories.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteInventorie, { input }),
            );
          }),
        ));

      // Outbound Shipments (by account, billToAccount, transferToAccount)
      const obRes = await API.graphql(
        graphqlOperation(queries.listOutboundShipments, {
          filter: {
            or: [
              { accountId: { eq: accountId } },
              { billToAccountId: { eq: accountId } },
              { transferToAccountId: { eq: accountId } },
            ],
          },
          limit: 1000,
        }),
      );
      const outboundShipments = obRes.data.listOutboundShipments.items;
      console.log("Delete OutboundShipments (by account): ", outboundShipments);
      deleteTrue &&
        (await Promise.allSettled(
          outboundShipments.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteOutboundShipment, { input }),
            );
          }),
        ));

      // Inbound Shipments (by account)
      const ibRes = await API.graphql(
        graphqlOperation(listIdQueries.listInboundShipmentIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const inboundShipments = ibRes.data.listInboundShipments.items;
      console.log("Delete InboundShipments (by account): ", inboundShipments);
      deleteTrue &&
        (await Promise.allSettled(
          inboundShipments.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteInboundShipment, { input }),
            );
          }),
        ));

      // Invoices (by account)
      const invoiceRes = await API.graphql(
        graphqlOperation(listIdQueries.listInvoiceIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const invoices = invoiceRes.data.listInvoices.items;
      console.log("Delete Invoices (by account): ", invoices);
      deleteTrue &&
        (await Promise.allSettled(
          invoices.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteInvoice, { input }),
            );
          }),
        ));

      // Services (by account)
      const svcRes = await API.graphql(
        graphqlOperation(listIdQueries.listServiceIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const services = svcRes.data.listServices.items;
      console.log("Delete Services (by account): ", services);
      deleteTrue &&
        (await Promise.allSettled(
          services.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteService, { input }),
            );
          }),
        ));

      // Products (by account)
      const prodRes = await API.graphql(
        graphqlOperation(listIdQueries.listProductIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const products = prodRes.data.listProducts.items;
      console.log("Delete Products (by account): ", products);
      deleteTrue &&
        (await Promise.allSettled(
          products.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteProduct, { input }),
            );
          }),
        ));

      // SavedAddresses (by account)
      const addrRes = await API.graphql(
        graphqlOperation(listIdQueries.listSavedAddressIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const savedAddresses = addrRes.data.listSavedAddresss.items;
      console.log("Delete SavedAddresses (by account): ", savedAddresses);
      deleteTrue &&
        (await Promise.allSettled(
          savedAddresses.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteSavedAddress, { input }),
            );
          }),
        ));

      // ChargeRules (by account)
      const chargeRes = await API.graphql(
        graphqlOperation(listIdQueries.listChargeRuleIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const chargeRules = chargeRes.data.listChargeRules.items;
      console.log("Delete ChargeRules (by account): ", chargeRules);
      deleteTrue &&
        (await Promise.allSettled(
          chargeRules.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteChargeRule, { input }),
            );
          }),
        ));

      // Timeslots (by account)
      const timeslotRes = await API.graphql(
        graphqlOperation(listIdQueries.listTimeslotIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const timeslots = timeslotRes.data.listTimeslots.items;
      console.log("Delete Timeslots (by account): ", timeslots);
      deleteTrue &&
        (await Promise.allSettled(
          timeslots.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteTimeslot, { input }),
            );
          }),
        ));

      // Seals (by account)
      const sealRes = await API.graphql(
        graphqlOperation(listIdQueries.listSealIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const seals = sealRes.data.listSeals.items;
      console.log("Delete Seals (by account): ", seals);
      deleteTrue &&
        (await Promise.allSettled(
          seals.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteSeal, { input }),
            );
          }),
        ));

      // CustomerReloadYardAccounts (by account)
      const cryaRes = await API.graphql(
        graphqlOperation(listIdQueries.listCustomerReloadYardAccountIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const customerReloadYardAccounts =
        cryaRes.data.listCustomerReloadYardAccounts.items;
      console.log(
        "Delete CustomerReloadYardAccounts (by account): ",
        customerReloadYardAccounts,
      );
      deleteTrue &&
        (await Promise.allSettled(
          customerReloadYardAccounts.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteCustomerReloadYardAccount, {
                input,
              }),
            );
          }),
        ));

      // Audits (by account)
      const auditRes = await API.graphql(
        graphqlOperation(queries.listAudits, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const audits = auditRes.data.listAudits.items;
      console.log("Delete Audits (by account): ", audits);
      deleteTrue &&
        (await Promise.allSettled(
          audits.map((item) => {
            const input = { id: item.id };
            return API.graphql(
              graphqlOperation(mutations.deleteAudit, { input }),
            );
          }),
        ));

      // Zones assigned to this account → delete their Locations, then Zones
      const zonesRes = await API.graphql(
        graphqlOperation(listIdQueries.listZoneIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const accountZones = zonesRes.data.listZones.items;
      console.log("Delete Locations & Zones (by account): ", accountZones);
      if (deleteTrue && accountZones.length) {
        // Delete Locations within each zone
        await Promise.allSettled(
          accountZones.map(async (zone) => {
            const locRes = await API.graphql(
              graphqlOperation(listIdQueries.listLocationIds, {
                filter: { zoneId: { eq: zone.id } },
                limit: 1000,
              }),
            );
            const zoneLocations = locRes.data.listLocations.items;
            await Promise.allSettled(
              zoneLocations.map((loc) => {
                const input = { id: loc.id };
                return API.graphql(
                  graphqlOperation(mutations.deleteLocation, { input }),
                );
              }),
            );
          }),
        );

        // Delete the zones themselves
        await Promise.allSettled(
          accountZones.map((z) => {
            const input = { id: z.id };
            return API.graphql(
              graphqlOperation(mutations.deleteZone, { input }),
            );
          }),
        );
      }

      // Locations assigned directly to this account (regardless of zone)
      const locByAccountRes = await API.graphql(
        graphqlOperation(listIdQueries.listLocationIds, {
          filter: { accountId: { eq: accountId } },
          limit: 1000,
        }),
      );
      const accountLocations = locByAccountRes.data.listLocations.items;
      console.log("Delete Locations (by accountId): ", accountLocations);
      deleteTrue &&
        (await Promise.allSettled(
          accountLocations.map((loc) => {
            const input = { id: loc.id };
            return API.graphql(
              graphqlOperation(mutations.deleteLocation, { input }),
            );
          }),
        ));

      deleteTrue &&
        (await (async () => {
          const deleteAccountMinimal = /* GraphQL */ `
            mutation DeleteAccount($input: DeleteAccountInput!) {
              deleteAccount(input: $input) {
                id
              }
            }
          `;
          return API.graphql(graphqlOperation(deleteAccountMinimal, { input }));
        })());
      console.log("Delete account finished  ");
    } catch (err) {
      console.log("Error deleting account: ", err);
      logError("admin:utils:deleteAccount", err, notify);
    }
    notify("Account deleted successfully");
  } catch (err) {
    logError("admin:utils:deleteAccount", err, notify);
    notify("Failed to delete account", "error");
  }
};
